import {
  KpiAreaValue,
  KpiType,
  UpdateKpiRequest,
  allStoreDetailsEndpoint,
  areaCapEndpoint,
  areaKpiEndpoint,
  areaTargetEndpoint,
  backofficeAIEndpoint,
  feedBackEndpoint,
  forecastEndpoint,
  highlightedWidgetsEndpoint,
  lastKpiEndpoint,
  nearbyStoresEndpoint,
  profitAndLossEndpoint,
  storeEndpoint,
  targetEndpoint,
  targetLockEndpoint,
  updateHighlightedWidgetsEndpoint,
  weatherWidgetEndpoint,
} from '../constants';
import { AllStoresDetails, Store, StoreDetails } from '../global-state/types';
import { useAuthAxiosRequest } from './useAuthAxiosRequest';
import { useUser } from './useController';
import { LastUpdatedTime, StorePerformanceRequest } from './useTransaction';

export type ChatBotType = {
  answer: string;
  prompts: string[];
};

export type WeatherResponse = {
  icon: string;
  name: string;
  situation: string;
  temperature: number;
  temperatureHigh: number;
  temperatureLow: number;
  units: string;
};

export type KpiAreaDetails = {
  kpiName: KpiType;
  kpiValues: KpiAreaValue[];
};

export type KpiDistribution = {
  timeUnit: string;
  value: number;
};

export type KpiTargetSource = 'BOX' | 'Synapps';

export type KpiTargetProps = {
  updated: string;
  target: number | null;
  isLocked: boolean;
  managedIn: KpiTargetSource;
  distributions: KpiDistribution[];
};

export type KpiTargetsProps = {
  [key in KpiType]: KpiTargetProps;
};

export type AreaTargetResponse = {
  storeId: string;
  currentTarget: number | null;
  recommendedTarget: number | null;
  storeName: string;
};

export type UpdateHighlightedWidgetsRequest = {
  [key in KpiType]?: boolean;
};

export type FeedbackData = {
  rating: number;
  comment: string;
  dateTime: string;
};

export type SummaryCostType = 'salesExcVat' | 'salesIncVat';

export type SummaryCostDetails = {
  actual: number;
  lastYear: number | null;
  budget: number | null;
};

export type SummaryCost = {
  [key in SummaryCostType]: SummaryCostDetails;
};

export type BreakdownCostType =
  | 'otherCosts'
  | 'reductions'
  | 'salaries'
  | 'scrapping'
  | 'shrinkage'
  | 'surplus1'
  | 'properties';

export type BreakdownCostDetails = SummaryCostDetails & { name: string };

export type BreakdownCost = {
  [key in BreakdownCostType]: BreakdownCostDetails[];
};

export type ProfitLossMonthlyBreakdown = {
  month: string;
  salesExcVat: number;
  expenses: number;
  surplus1: number;
};

export type ProfitLossResponse = LastUpdatedTime &
  SummaryCost & {
    details: BreakdownCost;
    monthlyBreakdown: ProfitLossMonthlyBreakdown[];
  };

export type LastKpiResponse = {
  current: {
    target: number | null;
    startDate: string;
  };
  last: {
    target: number;
    startDate: string;
    endDate: string;
  }[];
};

export const useStores = () => {
  const {
    get: { keyToken, currentStoreId },
  } = useUser();

  const { authAxiosRequest } = useAuthAxiosRequest(keyToken);

  const pingStores = async () => {
    const response = await authAxiosRequest<StoreDetails[]>(`${storeEndpoint}/ping`);
    return response;
  };

  const fetchAllStoresDetails = async () => {
    const allStoresDetailsResponse = await authAxiosRequest<AllStoresDetails>(
      `${storeEndpoint}${allStoreDetailsEndpoint}`,
    );

    return allStoresDetailsResponse;
  };

  const fetchStoresDetails = async () => {
    const storeResponse = await authAxiosRequest<StoreDetails[]>(`${storeEndpoint}/`);

    return storeResponse;
  };

  const fetchStoresData = async (storeId: string) => {
    const storeResponse = await authAxiosRequest<Store>(`${storeEndpoint}/${storeId}`);

    return storeResponse;
  };

  const fetchWeatherWidgetData = async () => {
    const weatherResponse = await authAxiosRequest<WeatherResponse>(
      `${storeEndpoint}/${currentStoreId}${weatherWidgetEndpoint}`,
    );

    return weatherResponse;
  };

  const fetchChatBotData = async (question: string) => {
    const chatBotResponse = await authAxiosRequest<ChatBotType>(
      `${storeEndpoint}/${currentStoreId}${backofficeAIEndpoint}`,
      {
        params: { question },
      },
    );

    return chatBotResponse;
  };

  const fetchKpiTargets = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<KpiTargetsProps>(
      `${storeEndpoint}/${currentStoreId}${targetEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchKpisAreaDetails = async (date?: string) => {
    const areaKpiResponse = await authAxiosRequest<KpiAreaDetails[]>(
      `${storeEndpoint}/${currentStoreId}${areaKpiEndpoint}`,
      {
        method: 'GET',
        params: {
          date: date,
        },
      },
    );

    return areaKpiResponse;
  };

  const updateKpiLock = async (request: UpdateKpiRequest[]) => {
    const kpiResponse = await authAxiosRequest<KpiTargetsProps>(
      `${storeEndpoint}/${currentStoreId}${targetEndpoint}${targetLockEndpoint}`,
      {
        method: 'PUT',
        data: request,
      },
    );

    return kpiResponse;
  };

  const updateKpiData = async (request: UpdateKpiRequest[]) => {
    const kpiResponse = await authAxiosRequest<KpiTargetsProps>(
      `${storeEndpoint}/${currentStoreId}${targetEndpoint}`,
      {
        method: 'PUT',
        data: request,
      },
    );

    return kpiResponse;
  };

  const removeKpiData = async (request: UpdateKpiRequest[]) => {
    const kpiResponse = await authAxiosRequest<KpiTargetsProps>(
      `${storeEndpoint}/${currentStoreId}${targetEndpoint}`,
      {
        method: 'DELETE',
        data: request,
      },
    );

    return kpiResponse;
  };

  const updateAreaCapData = async (request: UpdateKpiRequest[]) => {
    const kpiResponse = await authAxiosRequest<KpiAreaDetails[]>(
      `${storeEndpoint}${areaCapEndpoint.replace(':storeId', currentStoreId)}`,
      {
        method: 'PUT',
        data: request,
      },
    );

    return kpiResponse;
  };

  const removeAreaCapData = async (request: UpdateKpiRequest[]) => {
    const kpiResponse = await authAxiosRequest<KpiAreaDetails[]>(
      `${storeEndpoint}${areaCapEndpoint.replace(':storeId', currentStoreId)}`,
      {
        method: 'DELETE',
        data: request,
      },
    );

    return kpiResponse;
  };

  const fetchAreaStoresTarget = async (kpiName: string, date: string) => {
    const areaTargets = await authAxiosRequest<AreaTargetResponse[]>(
      `${storeEndpoint}${areaTargetEndpoint.replace(':storeId', currentStoreId).replace(':kpiName', kpiName)}`,
      {
        method: 'GET',
        params: {
          date: date,
        },
      },
    );

    return areaTargets;
  };

  const fetchNearbyStores = async () => {
    const response = await authAxiosRequest<StoreDetails[]>(
      `${storeEndpoint}/${currentStoreId}${nearbyStoresEndpoint}`,
    );

    return response;
  };

  const fetchHighlightedKpi = async () => {
    const highlightedWidgetResponse = await authAxiosRequest<KpiType[]>(
      `${storeEndpoint}/${currentStoreId}${highlightedWidgetsEndpoint}`,
    );
    return highlightedWidgetResponse;
  };

  const updateHighlightedKpi = async (request: UpdateHighlightedWidgetsRequest) => {
    const highlightedWidgetResponse = await authAxiosRequest<KpiType[]>(
      `${storeEndpoint}/${currentStoreId}${updateHighlightedWidgetsEndpoint}`,
      {
        method: 'PATCH',
        data: request,
      },
    );

    return highlightedWidgetResponse;
  };

  const fetchForecastForKpis = async <T>(kpiName: string) => {
    const forecastResponse = await authAxiosRequest<T[]>(
      `${storeEndpoint}/${currentStoreId}${forecastEndpoint}/${kpiName}`,
    );
    return forecastResponse;
  };

  const sendFeedbackData = async (feedbackData: FeedbackData) => {
    const response = await authAxiosRequest(`${storeEndpoint}/${currentStoreId}${feedBackEndpoint}`, {
      method: 'POST',
      data: feedbackData,
    });
    return response;
  };

  const fetchProfitLossData = async (monthArray: string[]) => {
    const params = new URLSearchParams();
    monthArray.forEach((month) => {
      params.append('months', month);
    });

    const response = await authAxiosRequest<ProfitLossResponse>(
      `${storeEndpoint}/${currentStoreId}${profitAndLossEndpoint}`,
      {
        method: 'GET',
        params: params,
      },
    );
    return response;
  };

  const fetchLastKpi = async (kpiName: string) => {
    const response = await authAxiosRequest<LastKpiResponse>(
      `${storeEndpoint}/${currentStoreId}${targetEndpoint}${lastKpiEndpoint}`,
      {
        method: 'GET',
        params: {
          kpiName,
        },
      },
    );

    return response;
  };

  return {
    pingStores,
    fetchAllStoresDetails,
    fetchStoresDetails,
    fetchStoresData,
    fetchWeatherWidgetData,
    fetchChatBotData,
    fetchKpisAreaDetails,
    fetchKpiTargets,
    updateKpiLock,
    updateKpiData,
    removeKpiData,
    updateAreaCapData,
    removeAreaCapData,
    fetchAreaStoresTarget,
    fetchNearbyStores,
    fetchHighlightedKpi,
    updateHighlightedKpi,
    fetchForecastForKpis,
    sendFeedbackData,
    fetchProfitLossData,
    fetchLastKpi,
  };
};
